import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { canBuyInApp } from '../utils/native';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Trial-period onboarding hint card. Reads /api/billing/me + /company-profile
 * once and renders a small checklist of remaining setup steps. Each row is
 * a deep link, so the buyer can tick items off without hunting through menus.
 *
 * Skipped entirely when:
 *  - Tenant is not in 'trial' state (covered by other UX in active/expired)
 *  - Superuser (no tenant scope)
 *  - All checklist items already done
 *
 * Best-effort: any fetch failure silently hides the card.
 */
const OnboardingChecklist = () => {
  const { t } = useTranslation();
  const [me, setMe] = useState(null);
  const [profile, setProfile] = useState(null);
  const [dismissed, setDismissed] = useState(
    localStorage.getItem('onboarding_dismissed') === '1'
  );

  useEffect(() => {
    if (dismissed) return;
    let cancelled = false;
    (async () => {
      try {
        const [m, p] = await Promise.all([
          axios.get(`${API}/api/billing/me`).catch(() => ({ data: null })),
          axios.get(`${API}/api/billing/company-profile`).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setMe(m.data);
        setProfile(p.data);
      } catch {
        // silent
      }
    })();
    return () => { cancelled = true; };
  }, [dismissed]);

  // every step but the company profile is a checkout step
  if (dismissed || !me || me.license_status !== 'trial' || !canBuyInApp()) return null;

  const items = [
    {
      key: 'profile',
      label: t('shell.onboarding.profile'),
      done: !!(profile?.tax_number || profile?.identity_number),
      to: '/modules/admin/billing',
    },
    {
      key: 'modules',
      label: t('shell.onboarding.modules'),
      done: (me.active_modules || []).length > 0,
      to: '/checkout',
    },
    {
      key: 'card',
      label: t('shell.onboarding.card'),
      done: !!me.saved_card?.present,
      to: '/checkout',
    },
    {
      key: 'license',
      label: t('shell.onboarding.license'),
      done: false,  // by definition trial → not yet
      to: '/checkout',
    },
  ];
  const allDone = items.every((i) => i.done);
  if (allDone) return null;

  const trialDaysLeft = me.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(me.trial_ends_at).getTime() - Date.now()) / 86400000))
    : null;

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-blue-900">🚀 {t('shell.onboarding.title')}</h2>
          <p className="text-sm text-blue-700/80 mt-0.5">
            {trialDaysLeft != null
              ? t('shell.onboarding.trialDaysLeft', { count: trialDaysLeft })
              : t('shell.onboarding.activateAccount')}
          </p>
        </div>
        <button onClick={() => { localStorage.setItem('onboarding_dismissed', '1'); setDismissed(true); }}
                className="text-xs text-blue-600/70 hover:text-blue-900">
          {t('shell.onboarding.hide')}
        </button>
      </div>
      <ul className="mt-4 space-y-2">
        {items.map((it) => (
          <li key={it.key} className="flex items-center gap-3">
            <span className={`flex-shrink-0 w-5 h-5 rounded-full border-2 ${
              it.done
                ? 'bg-green-500 border-green-500 text-white text-xs flex items-center justify-center'
                : 'border-blue-300 bg-white'
            }`}>
              {it.done && '✓'}
            </span>
            {it.done ? (
              <span className="text-sm text-gray-500 line-through">{it.label}</span>
            ) : (
              <Link to={it.to} className="text-sm text-blue-700 hover:underline">{it.label}</Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default OnboardingChecklist;
